import React, { useEffect, useMemo, useState } from 'react';
import Joyride, { type CallBackProps, type Step, STATUS, EVENTS } from 'react-joyride';
import { TOURS, type TourName } from '../../tooltips/copy';
import { useTours, useMarkTourComplete } from '../../hooks/useTours';
import { useBranding } from '../../contexts/BrandingContext';

export interface TourProps {
  /** Name of the tour to run. Must be a key in the shared TOURS copy map. */
  name: TourName;
  /**
   * When true, always run the tour (used by the Settings "Replay" flow).
   * When false/undefined, only runs if the user hasn't completed it yet
   * OR if the URL has `?replay=<name>` matching this tour.
   */
  forceRun?: boolean;
  /**
   * Called when the tour finishes (completed or skipped). Lets a parent
   * clear any force-run state so Replay can fire again later.
   */
  onFinished?: () => void;
}

/**
 * Reads `?replay=<name>` from window.location and strips it from the URL
 * once consumed so the tour doesn't re-fire on navigation. Returns the
 * requested replay name, or null.
 */
function useReplayQuery(): string | null {
  const [replay, setReplay] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('replay');
  });
  useEffect(() => {
    if (replay && typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('replay');
      window.history.replaceState({}, '', url.toString());
      // Keep the value in state so forceRun stays true for THIS mount
    }
  }, [replay]);
  return replay;
}

/**
 * First-time-user product tour. Reads tours_seen via react-query, renders
 * a Joyride with steps from the shared TOURS copy, and POSTs completion
 * to the backend so it doesn't re-fire across devices.
 *
 * Targets each step to a `data-tour-id="..."` attribute on the surface's
 * elements. If any target doesn't render on the page this tour mounts on,
 * Joyride gracefully skips that step (floater falls back to center).
 */
export const Tour: React.FC<TourProps> = ({ name, forceRun, onFinished }) => {
  const { data: toursSeen, isLoading } = useTours();
  const markComplete = useMarkTourComplete();
  const replay = useReplayQuery();
  const { name: brandName } = useBranding();

  const steps = useMemo<Step[]>(
    () =>
      // V1.5.M10 B-P2: the shared tour copy (kept byte-identical with
      // 2connect-mobile-app) hardcodes the platform name; substitute the active
      // brand so a white-label host's tour reads "Welcome to <client>". No-op on
      // the default brand (brandName === "2Connect Professional").
      TOURS[name].map(step => ({
        target: step.target,
        title: step.title.replace(/2Connect Professional/g, brandName),
        content: step.body.replace(/2Connect Professional/g, brandName),
        placement: step.placement ?? 'auto',
        disableBeacon: true,
      })),
    [name, brandName]
  );

  // Don't fire until we know whether the user has seen this tour.
  // Avoids the "flash of tour" that then immediately dismisses itself.
  if (isLoading) return null;

  const alreadySeen = Boolean(toursSeen?.[name]);
  const replayRequested = replay === name;
  const run = Boolean(forceRun) || replayRequested || !alreadySeen;

  if (!run) return null;

  const handleCallback = (data: CallBackProps) => {
    const { status, type } = data;
    const finished: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    if (finished.includes(status)) {
      // Only mark seen on a fresh auto-run; Replay / forceRun doesn't
      // re-record since it's already in tours_seen.
      if (!alreadySeen && !replayRequested && !forceRun) {
        markComplete.mutate(name);
      }
      onFinished?.();
      return;
    }
    if (type === EVENTS.TOUR_END) {
      onFinished?.();
    }
  };

  // V1.5.M10 B-P2: theme the tour from the live brand CSS vars so a white-label
  // subdomain's tour matches its palette instead of staying 2Connect purple.
  // The vars are always set (compiled @theme default = the original #6638A6 /
  // #522D85, so the non-branded tour is byte-identical); BrandingProvider
  // overrides them on a client host. Joyride needs concrete color strings, not
  // var() references, so we read the computed values here.
  const brandVar = (name: string, fallback: string): string => {
    if (typeof document === 'undefined') return fallback;
    return (
      getComputedStyle(document.documentElement).getPropertyValue(name).trim() ||
      fallback
    );
  };
  const brandPrimary = brandVar('--color-primary-500', '#6638A6');
  const brandPrimaryDark = brandVar('--color-primary-600', '#522D85');

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      disableScrolling={false}
      callback={handleCallback}
      styles={{
        options: {
          primaryColor: brandPrimary, // brand primary (white-label aware)
          textColor: '#364151',
          backgroundColor: '#ffffff',
          arrowColor: '#ffffff',
          // Apr-25: lightened + neutralized overlay. Was rgba(25, 13, 87, 0.55)
          // — primary-indigo at 55% which washed the entire viewport in brand
          // color, hid the "Match search in progress" banner and dashboard
          // cards underneath, and added mood pressure at a moment that should
          // feel calm. Neutral slate-900 at 30% keeps focus on the tour modal
          // without competing with the product surface.
          overlayColor: 'rgba(15, 23, 42, 0.30)',
          zIndex: 10000,
        },
        // Jun-16 (a user V1 test, B1): round the tour card button to match the
        // rounded-xl match cards; Joyride defaults to near-square corners which
        // looked off-brand.
        tooltip: {
          borderRadius: 12,
        },
        buttonNext: {
          backgroundColor: brandPrimary,
          borderRadius: 8,
        },
        buttonBack: {
          color: brandPrimaryDark,
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Got it',
        next: 'Next',
        skip: 'Skip tour',
      }}
    />
  );
};
